import { useEffect, useMemo, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHelpStore } from '@/shared/store/helpStore';
import { HELP, findArticle, searchArticles, HelpArticle } from './help-content';
import { IconHelp, IconBook, IconSearch, IconChevron, IconSpark, IconX } from '@/components/ui/Icon';
import { ASSISTANT_NAME } from '@/shared/branding';

// ---- tiny markdown subset renderer (matches help-content.ts authoring) ----
// Inline: **bold**, `code`, [links](/to) and *italics* (an asterisk next to a
// space is no italics: "a * b" stays as typed).
function renderInline(text: string, onNavigate: (to: string) => void): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|\*(?![\s*])([^*]*?[^\s*])\*)/g;
  let last = 0; let m: RegExpExecArray | null; let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[2] !== undefined) out.push(<strong key={k++} className="font-semibold text-primary-text">{m[2]}</strong>);
    else if (m[3] !== undefined) out.push(<code key={k++} className="px-1 py-0.5 rounded bg-slate-100 text-[0.75rem] font-numeric text-primary-text">{m[3]}</code>);
    else if (m[6] !== undefined) out.push(<em key={k++}>{m[6]}</em>);
    else {
      const label = m[4]; const url = m[5];
      const internal = url.startsWith('/');
      out.push(
        <a key={k++} href={url} target={internal ? undefined : '_blank'} rel="noreferrer"
          onClick={(e) => { if (internal) { e.preventDefault(); onNavigate(url); } }}
          className="text-primary font-medium hover:underline">{label}</a>,
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function renderBody(body: string, onNavigate: (to: string) => void): ReactNode {
  const lines = body.split('\n');
  const blocks: ReactNode[] = [];
  let i = 0; let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i++; continue; }
    if (line.startsWith('## ')) {
      blocks.push(<h3 key={key++} className="text-[0.8125rem] font-semibold text-primary-text mt-4 mb-1.5">{line.slice(3)}</h3>);
      i++; continue;
    }
    // A fenced block (``` … ```) shows as written: code, e.g. a procedure's JSON.
    if (line.startsWith('```')) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) { code.push(lines[i]); i++; }
      i++;
      blocks.push(<pre key={key++} className="my-1.5 rounded-md border border-border-light bg-slate-50 p-2 text-[0.75rem] font-mono text-primary-text overflow-x-auto whitespace-pre">{code.join('\n')}</pre>);
      continue;
    }
    if (/^- /.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^- /.test(lines[i])) { items.push(lines[i].slice(2)); i++; }
      blocks.push(<ul key={key++} className="list-disc pl-5 space-y-1 my-1.5">{items.map((t, j) => <li key={j}>{renderInline(t, onNavigate)}</li>)}</ul>);
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) { items.push(lines[i].replace(/^\d+\.\s/, '')); i++; }
      blocks.push(<ol key={key++} className="list-decimal pl-5 space-y-1 my-1.5">{items.map((t, j) => <li key={j}>{renderInline(t, onNavigate)}</li>)}</ol>);
      continue;
    }
    // paragraph: gather consecutive plain lines
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !lines[i].startsWith('## ') && !lines[i].startsWith('```') && !/^- /.test(lines[i]) && !/^\d+\.\s/.test(lines[i])) { para.push(lines[i]); i++; }
    blocks.push(<p key={key++} className="my-1.5 leading-relaxed">{renderInline(para.join(' '), onNavigate)}</p>);
  }
  return <div className="text-[0.8125rem] text-secondary">{blocks}</div>;
}

export function HelpCenter() {
  const open = useHelpStore((s) => s.open);
  const topic = useHelpStore((s) => s.topic);
  const close = useHelpStore((s) => s.close);
  const nav = useNavigate();

  const [selected, setSelected] = useState<string | null>(null);
  const [q, setQ] = useState('');

  // When opened (or the deep-link topic changes), land on the requested article.
  useEffect(() => {
    if (open) { setSelected(topic); setQ(''); }
  }, [open, topic]);

  // Esc closes, unless another layer (a dialog drawn over Help) already used it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const results = useMemo(() => (q.trim() ? searchArticles(q) : []), [q]);
  const goTo = (to: string) => { close(); nav(to); };
  const askAi = () => { close(); window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true })); };

  if (!open) return null;
  const current = selected ? findArticle(selected) : null;

  return (
    <div className="fixed inset-0 z-drawer flex justify-end" role="dialog" aria-modal="true"
      onClick={close}>
      <div className="absolute inset-0 bg-black/10" />
      <aside onClick={(e) => e.stopPropagation()}
        className="relative h-full w-full sm:w-[520px] bg-surface shadow-2xl border-l border-border flex flex-col animate-[slideIn_.18s_ease]">
        {/* header */}
        <div className="flex items-center justify-between px-4 h-12 border-b border-border shrink-0">
          <div className="flex items-center gap-2 text-[0.875rem] font-semibold text-primary-text">
            <span className="text-primary"><IconHelp size={17} /></span> Help Center
          </div>
          <button aria-label="Close" onClick={close} className="text-muted hover:text-primary-text"><IconX size={18} /></button>
        </div>

        {/* search */}
        <div className="px-4 py-2.5 border-b border-border-light shrink-0">
          <div className="relative">
            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted"><IconSearch size={14} /></span>
            <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setSelected(null); }}
              placeholder="Search help…"
              className="w-full h-9 pl-8 pr-3 rounded-lg border border-border bg-surface text-[0.8125rem] outline-none focus:border-primary" />
          </div>
        </div>

        {/* body */}
        <div className="flex-1 min-h-0 overflow-auto px-4 py-3">
          {current ? (
            <article>
              <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1 text-[0.75rem] text-secondary hover:text-primary-text mb-2">
                <span className="rotate-90 inline-flex"><IconChevron size={12} /></span> All topics
              </button>
              <h2 className="text-[1rem] font-semibold text-primary-text">{current.art.title}</h2>
              <p className="text-[0.75rem] text-muted mt-0.5 mb-1">{current.cat.title}</p>
              {renderBody(current.art.body, goTo)}
            </article>
          ) : q.trim() ? (
            <div>
              <p className="text-[0.75rem] text-muted mb-2">{results.length} result{results.length === 1 ? '' : 's'} for “{q}”</p>
              <ul className="space-y-1.5">
                {results.map((a) => <ArticleRow key={a.id} art={a} onClick={() => setSelected(a.id)} />)}
                {results.length === 0 && (
                  <li className="text-[0.8125rem] text-secondary py-6 text-center">
                    Nothing found. Try different words, or <button onClick={askAi} className="text-primary font-medium hover:underline">ask the {ASSISTANT_NAME}</button>.
                  </li>
                )}
              </ul>
            </div>
          ) : (
            <div className="space-y-4">
              {HELP.map((cat) => (
                <section key={cat.id}>
                  <h3 className="text-[0.6875rem] font-semibold uppercase tracking-wide text-muted mb-1.5 flex items-center gap-1.5">
                    <IconBook size={12} /> {cat.title}
                  </h3>
                  <ul className="space-y-1.5">
                    {cat.articles.map((a) => <ArticleRow key={a.id} art={a} onClick={() => setSelected(a.id)} />)}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </div>

        {/* footer */}
        <div className="px-4 py-3 border-t border-border-light shrink-0 bg-slate-50/60">
          <button onClick={askAi}
            className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-lg bg-primary text-white text-[0.8125rem] font-semibold hover:brightness-110 transition">
            <IconSpark size={15} /> Still stuck? Ask the {ASSISTANT_NAME}
          </button>
        </div>
      </aside>
      <style>{`@keyframes slideIn{from{transform:translateX(16px);opacity:.6}to{transform:translateX(0);opacity:1}}`}</style>
    </div>
  );
}

function ArticleRow({ art, onClick }: { art: HelpArticle; onClick: () => void }) {
  return (
    <li>
      <button onClick={onClick}
        className="w-full text-left rounded-lg border border-border-light hover:border-primary/40 hover:bg-primary/[0.03] transition px-3 py-2 group">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[0.8125rem] font-medium text-primary-text">{art.title}</span>
          <span className="text-muted group-hover:text-primary transition shrink-0"><IconChevron size={13} /></span>
        </div>
        <p className="text-[0.75rem] text-secondary mt-0.5 leading-snug">{art.summary}</p>
      </button>
    </li>
  );
}
